/**
 * O cartão da ficha do associado (spec 031, §5) — nas DUAS visões do ticket:
 * a aba Informações da completa e o cartão da Solicitação da compacta.
 *
 * - cada valor leva a origem: "da descrição", "do título", "do solicitante",
 *   "do recurso" ou "editado por X em …"; quando a pessoa escreveu algo que a
 *   descrição diz diferente, "Na descrição: X" (a descrição continua lá para
 *   comparar — editar a ficha não a reescreve, D4);
 * - um lápis edita o cartão inteiro (`tickets.edit`; no ticket fechado,
 *   `tickets.edit_closed` — a ficha é correção de registro, como o solicitante);
 *   "voltar ao da descrição" põe o slot em NULL;
 * - no ticket espelhado do TiFlux, só leitura (a API recusaria com 409);
 * - só nos perfis em que o recurso sai do atendimento (`ticket_identifier`, o
 *   mesmo portão do editor da descrição); no genérico nada muda.
 *
 * A regra mora em ticket-sheet-view (puro, testado na API).
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { dispatchSheetErrorMessage, type TicketDetail, type TicketSheetSlot } from '@erp/shared';
import { Link } from '@tanstack/react-router';
import { ChevronDown, Lock, Pencil, RefreshCw, RotateCcw } from 'lucide-react';
import { Spinner } from '@/components/ui/misc';
import { useSgr, useSheetSgrRefresh } from '@/features/sgr/sgr-queries';
import { sgrLinkLabels, sheetRefreshText, vehicleLinkTarget } from '@/features/sgr/sgr-view';
import { useOpenSgrVehicle } from '@/features/sgr/use-open-vehicle';
import { usePermissions } from '@/auth/use-permissions';
import { Button } from '@/components/ui/button';
import { Input, Label } from '@/components/ui/input';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import { useTenantProfile } from '@/shared/tenant-profile';
import { usePatchTicket } from './tickets-mutations';
import { desksListQuery } from './tickets-queries';
import { errorMessage, isTicketMirrored } from './ticket-format';
import {
  PHONE_HINT_TEXT,
  SHEET_MAIN_SLOTS,
  SHEET_MORE_SLOTS,
  phoneHint,
  sheetCard,
  sheetEditDraft,
  sheetEditPatch,
  sheetLabel,
  sheetMaxLength,
  sheetEditProblem,
  sheetOfTicket,
  sheetTitle,
  sgrDriftText,
  type SheetCardRow,
} from './ticket-sheet-view';

/** o perfil trabalha com a ficha (o recurso é o identificador do atendimento) */
export function useSheetProfile(): boolean {
  return useTenantProfile().resource.source === 'ticket_identifier';
}

export function TicketSheetCard({
  ticket,
  variant = 'card',
}: {
  ticket: TicketDetail;
  /** `card`: o cartão da aba Informações; `inline`: dentro da Solicitação */
  variant?: 'card' | 'inline';
}) {
  const profile = useTenantProfile();
  const { can, ready } = usePermissions();
  const [editing, setEditing] = useState(false);
  // spec 032: a foto do SGR, o "Atualizar do SGR" e os links do veículo e do associado
  const sgr = useSgr();
  const refresh = useSheetSgrRefresh(ticket.ticketNumber);
  const slotPatch = usePatchTicket(ticket.ticketNumber);
  const openVehicle = useOpenSgrVehicle(sgr.sourceName);
  const [refreshNote, setRefreshNote] = useState<string | null>(null);
  if (profile.resource.source !== 'ticket_identifier') return null;

  const card = sheetCard(ticket, profile.terms, sgr.sourceName || undefined);
  const mirrored = isTicketMirrored(ticket);
  const closed = ticket.status.kind === 'closed' || ticket.status.kind === 'canceled';
  const canEdit =
    ready && !mirrored && can('tickets.edit') && (!closed || can('tickets.edit_closed'));
  const photo = ticket.sheetFromSgr ?? null;
  const canRefresh = canEdit && photo !== null && sgr.available;
  const labels = sgrLinkLabels(profile.terms);
  const target = sgr.available
    ? vehicleLinkTarget({
        equipment: ticket.equipment,
        equipmentIsSgr: (ticket.endCustomer ?? null) !== null,
        sgrId: card.meta.find((m) => m.label === 'Código do cadastro')?.value ?? null,
        plate: card.rows.find((r) => r.slot === 'plate')?.value ?? null,
      })
    : null;
  const canSeeVehicle = ready && can('resources.view');
  const canSeeContact = ready && (can('tickets.view_open') || can('clients.view'));
  // spec 035 (D5): o SGR mudou desde a foto — no editável, o "Atualizar do SGR"
  // ao lado aplica; no espelhado é só aviso (ali o ERP não edita)
  const driftText = sgr.available ? sgrDriftText(ticket, sgr.sourceName, (slot) => sheetLabel(slot, profile.terms)) : null;
  const adoptSgrValue =
    canEdit && sgr.available
      ? (slot: TicketSheetSlot, value: string) => slotPatch.mutate({ sheet: { [slot]: value } })
      : null;

  const header = (
    <div className="mb-2 flex items-center justify-between gap-2">
      <h2 className={cn('font-semibold', variant === 'card' ? 'text-base' : 'text-sm')}>
        {sheetTitle(profile.terms)}
      </h2>
      {!editing && canEdit ? (
        <Button
          variant="ghost"
          size="icon-xs"
          className="text-muted-foreground"
          aria-label={`Editar a ${sheetTitle(profile.terms).toLowerCase()}`}
          title="Editar a ficha"
          onClick={() => setEditing(true)}
        >
          <Pencil />
        </Button>
      ) : null}
      {mirrored ? (
        <span className="inline-flex items-center gap-1 text-xs text-muted-foreground" title="Espelhado do TiFlux">
          <Lock className="size-3" aria-hidden />
          acompanha a descrição do TiFlux
        </span>
      ) : null}
      {!editing && canRefresh ? (
        <Button
          variant="ghost"
          size="xs"
          className="text-muted-foreground"
          disabled={refresh.isPending}
          onClick={() => {
            setRefreshNote(null);
            refresh.mutate(undefined, {
              onSuccess: (res) =>
                setRefreshNote(sheetRefreshText(res, sgr.sourceName, (slot) => sheetLabel(slot, profile.terms))),
              onError: (e) => setRefreshNote(errorMessage(e)),
            });
          }}
        >
          {refresh.isPending ? <Spinner className="size-3.5" /> : <RefreshCw className="size-3.5" />}
          {`Atualizar do ${sgr.sourceName}`}
        </Button>
      ) : null}
    </div>
  );

  const links =
    target && (canSeeVehicle || canSeeContact) ? (
      <div className="mt-2 flex flex-wrap items-center gap-3 text-xs">
        {canSeeVehicle ? (
          target.kind === 'equipment' ? (
            <Link
              to="/resources/$resourceId"
              params={{ resourceId: String(target.equipmentId) }}
              className="font-medium text-primary hover:underline"
            >
              {labels.vehicle}
            </Link>
          ) : (
            <button
              type="button"
              className="font-medium text-primary hover:underline disabled:opacity-60"
              disabled={openVehicle.busy}
              onClick={() => void openVehicle.open(target, 'vehicle')}
            >
              {labels.vehicle}
            </button>
          )
        ) : null}
        {canSeeContact ? (
          ticket.endCustomer ? (
            <Link
              to="/end-customers/$endCustomerId"
              params={{ endCustomerId: String(ticket.endCustomer.id) }}
              className="font-medium text-primary hover:underline"
            >
              {labels.endCustomer}
            </Link>
          ) : (
            <button
              type="button"
              className="font-medium text-primary hover:underline disabled:opacity-60"
              disabled={openVehicle.busy}
              onClick={() => void openVehicle.open(target, 'end-customer')}
            >
              {labels.endCustomer}
            </button>
          )
        ) : null}
        {openVehicle.busy ? <Spinner className="size-3.5" /> : null}
        {openVehicle.error ? <span className="text-amber-700 dark:text-amber-300">{openVehicle.error}</span> : null}
      </div>
    ) : null;

  const body = editing ? (
    <SheetEditor ticket={ticket} sourceName={sgr.sourceName} onDone={() => setEditing(false)} />
  ) : (
    <>
      {driftText ? (
        <p role="status" className="mb-2 text-xs text-amber-700 dark:text-amber-300">
          {driftText}
          {canRefresh ? ` Use "Atualizar do ${sgr.sourceName}" para trazer o que não foi editado.` : ''}
        </p>
      ) : null}
      <SheetView rows={card.rows} meta={card.meta} sourceName={sgr.sourceName} onUseSgr={adoptSgrValue} />
      {refreshNote ? (
        <p role="status" className="mt-2 text-xs text-muted-foreground">
          {refreshNote}
        </p>
      ) : null}
      {links}
    </>
  );

  if (variant === 'inline') {
    return (
      <div aria-label={sheetTitle(profile.terms)} className="space-y-1">
        {header}
        {body}
      </div>
    );
  }
  return (
    <section
      aria-label={sheetTitle(profile.terms)}
      className="rounded-lg border bg-card p-4 text-card-foreground shadow-sm"
    >
      {header}
      {body}
    </section>
  );
}

function SheetView({
  rows,
  meta,
  sourceName,
  onUseSgr,
}: {
  rows: SheetCardRow[];
  meta: Array<{ label: string; value: string }>;
  sourceName: string;
  /** "usar" o valor do SGR num slot (null = só leitura) */
  onUseSgr: ((slot: TicketSheetSlot, value: string) => void) | null;
}) {
  return (
    <dl className="grid grid-cols-[minmax(7rem,auto)_1fr] gap-x-4 gap-y-1.5 text-sm">
      {rows.map((r) => (
        <div key={r.slot} className="contents">
          <dt className="pt-px text-muted-foreground">{r.label}</dt>
          <dd className="min-w-0 break-words">
            <span className={r.value === null ? 'text-muted-foreground' : 'text-foreground'}>
              {r.value ?? '—'}
            </span>
            {r.origin ? (
              <span
                className={cn('ml-1.5 text-xs', r.edited && !r.fromSgr ? 'text-primary' : 'text-muted-foreground')}
              >
                {/* "do SGR (06/10, por Maria)" já traz os parênteses dele (spec 032, §5) */}
                {r.fromSgr ? r.origin : `(${r.origin})`}
              </span>
            ) : null}
            {r.differs ? (
              <span className="block text-xs text-amber-700 dark:text-amber-300">{r.differs}</span>
            ) : null}
            {r.sgrValue !== null && sourceName ? (
              <span className="block text-xs text-amber-700 dark:text-amber-300">
                {`No ${sourceName}: ${r.sgrValue}`}
                {onUseSgr ? (
                  <button
                    type="button"
                    className="ml-1.5 font-medium text-primary hover:underline"
                    onClick={() => onUseSgr(r.slot, r.sgrValue!)}
                  >
                    usar
                  </button>
                ) : null}
              </span>
            ) : null}
          </dd>
        </div>
      ))}
      {meta.map((m) => (
        <div key={m.label} className="contents">
          <dt className="text-muted-foreground">{m.label}</dt>
          <dd className="min-w-0 break-words text-muted-foreground">{m.value}</dd>
        </div>
      ))}
    </dl>
  );
}

function SheetEditor({ ticket, sourceName, onDone }: { ticket: TicketDetail; sourceName: string; onDone: () => void }) {
  const profile = useTenantProfile();
  const patch = usePatchTicket(ticket.ticketNumber);
  const card = sheetCard(ticket, profile.terms);
  const stored = sheetOfTicket(ticket);
  const [draft, setDraft] = useState(() => sheetEditDraft(ticket));
  const [reset, setReset] = useState<Set<TicketSheetSlot>>(() => new Set());
  const anyMoreFilled = SHEET_MORE_SLOTS.some((s) => draft[s] !== '' || stored[s] !== null);
  const [moreOpen, setMoreOpen] = useState(anyMoreFilled);
  // spec 031 (D5): o que o disparo da mesa exige — a mesma lista de mesas do
  // "Novo ticket" (cache compartilhado)
  const desksQ = useQuery(desksListQuery());
  const desk = desksQ.data?.find((d) => d.id === ticket.desk.id) ?? null;
  const [blocked, setBlocked] = useState<string | null>(null);

  const rowOf = (slot: TicketSheetSlot) => card.rows.find((r) => r.slot === slot);

  const save = () => {
    const body = sheetEditPatch(ticket, draft, reset);
    if (!body) {
      onDone();
      return;
    }
    // o mesmo juiz da API, antes do envio: apagar o telefone de um ticket numa
    // mesa que manda WhatsApp é recusado (422) — diz aqui, sem ida e volta
    const problem = sheetEditProblem(ticket, body, desk?.dispatchSheet);
    if (problem) {
      setBlocked(dispatchSheetErrorMessage(problem, desk?.displayName, profile.terms));
      return;
    }
    setBlocked(null);
    patch.mutate(body, { onSuccess: onDone });
  };

  const field = (slot: TicketSheetSlot) => {
    const id = `sheet-${ticket.ticketNumber}-${slot}`;
    const isReset = reset.has(slot);
    const row = rowOf(slot);
    const hint = slot === 'phone' || slot === 'phoneAlt' ? phoneHint(draft[slot]) : null;
    const photoValue = ticket.sheetFromSgr?.slots[slot] ?? null;
    return (
      <div key={slot} className="space-y-1">
        <Label htmlFor={id} className="text-xs">
          {sheetLabel(slot, profile.terms)}
        </Label>
        <Input
          id={id}
          className="h-8"
          value={isReset ? '' : draft[slot]}
          placeholder={isReset ? 'segue a descrição' : undefined}
          disabled={isReset}
          maxLength={sheetMaxLength(slot)}
          onChange={(e) => setDraft((d) => ({ ...d, [slot]: e.target.value }))}
        />
        {hint && !isReset ? (
          <p className={cn('text-xs', hint === 'ok' ? 'text-muted-foreground' : 'text-amber-700 dark:text-amber-300')}>
            {PHONE_HINT_TEXT[hint]}
          </p>
        ) : null}
        {stored[slot] !== null ? (
          <button
            type="button"
            className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
            onClick={() =>
              setReset((r) => {
                const next = new Set(r);
                if (next.has(slot)) next.delete(slot);
                else next.add(slot);
                return next;
              })
            }
          >
            <RotateCcw className="size-3" aria-hidden />
            {isReset ? 'manter o editado' : 'voltar ao da descrição'}
          </button>
        ) : null}
        {row?.differs && !isReset ? <p className="text-xs text-amber-700 dark:text-amber-300">{row.differs}</p> : null}
        {/* spec 032: "Voltar ao do SGR" repõe o valor da foto */}
        {photoValue !== null && photoValue !== '' && draft[slot] !== photoValue && sourceName ? (
          <button
            type="button"
            className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
            onClick={() => {
              setDraft((d) => ({ ...d, [slot]: photoValue }));
              setReset((r) => {
                const next = new Set(r);
                next.delete(slot);
                return next;
              });
            }}
          >
            <RotateCcw className="size-3" aria-hidden />
            {`voltar ao do ${sourceName}`}
          </button>
        ) : null}
      </div>
    );
  };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 gap-x-3 gap-y-2 sm:grid-cols-2">{SHEET_MAIN_SLOTS.map(field)}</div>
      <Collapsible open={moreOpen} onOpenChange={setMoreOpen}>
        <CollapsibleTrigger className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline">
          <ChevronDown className={cn('size-3.5 transition-transform', moreOpen && 'rotate-180')} aria-hidden />
          Mais dados da ficha
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="mt-2 grid grid-cols-1 gap-x-3 gap-y-2 sm:grid-cols-2">{SHEET_MORE_SLOTS.map(field)}</div>
        </CollapsibleContent>
      </Collapsible>
      {blocked ? (
        <p role="alert" className="text-sm text-destructive">
          {blocked}
        </p>
      ) : patch.isError ? (
        <p role="alert" className="text-sm text-destructive">
          {errorMessage(patch.error)}
        </p>
      ) : null}
      <div className="flex justify-end gap-2">
        <Button variant="outline" size="sm" disabled={patch.isPending} onClick={onDone}>
          Cancelar
        </Button>
        <Button size="sm" disabled={patch.isPending} onClick={save}>
          Salvar
        </Button>
      </div>
    </div>
  );
}
